import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, BookOpen, ChevronDown, Clock3, Search } from "lucide-react";
import { Badge, Button, Field, FilterBar, SegmentedControl, Select } from "@studio";
import { SeoNotice } from "@studio-features/meta-crew/seo-shared";
import { competitionAreas } from "@studio-features/meta-crew/competition-data";
import { CompetitionSourceBoard, KeywordCompetitionView } from "@studio-features/meta-crew/seo-competition";
import { api, errorMessage } from "../lib/api";
import { can } from "../lib/access";
import { fmtDateTime, num } from "../lib/format";
import { useAction, useAdminSeoKeywords, useCustomers } from "../lib/queries";
import { useCompetitionKeyword, useCompetitionSources, useProbe, useProbes, type ProbeView } from "../lib/competition-api";
import type { Me } from "../lib/types";
import { Empty, Loading, Panel, PanelError } from "../console/ui";

/**
 * 관리 콘솔 › 분석 › 경쟁 분석 (docs/meta-crew-seo-competition-design.md, 시안 014da89 seo-competition.tsx 를 실제 API 로).
 * - 고객사 키워드: 경쟁 현황판(작성처별 상위 등장) → 키워드 경쟁 분석(상위 글 vs 우리 글).
 * - 자유 분석: 등록되지 않은 키워드를 바로 조회 (직원 누구나, 검색 API 1회). 다시 분석은 관리자·운영자(COLLECTOR_MANAGE)만.
 * 순위는 검색 API 순서, 글 지표는 공개 페이지에서 센 값. 시안 부품은 서버 응답 모양을 그대로 받는다.
 */
export function CompetitionScreen({ me }: { me: Me }) {
  const [mode, setMode] = useState<"customer" | "probe">("customer");
  const [area, setArea] = useState("blog");
  const [days, setDays] = useState(30);
  const customers = useCustomers("", true);
  const [customerId, setCustomerId] = useState("");
  const list = customers.data?.items ?? [];
  useEffect(() => {
    if (!customerId && list[0]) setCustomerId(list[0].id);
  }, [customerId, list]);

  return (
    <div className="competition-screen">
      <div className="competition-toolbar">
        <div className="competition-mode">
          <span>분석 방식</span>
          <SegmentedControl
            label="분석 방식"
            value={mode}
            onChange={setMode}
            options={[
              { value: "customer", label: "고객사 키워드" },
              { value: "probe", label: "자유 분석" },
            ]}
          />
        </div>
        <FilterBar>
          {mode === "customer" && (
            <Select label="고객사" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
          <Select label="검색 영역" value={area} onChange={(e) => setArea(e.target.value)}>
            {Object.entries(competitionAreas).map(([v, n]) => (
              <option value={v} key={v}>
                {n}
              </option>
            ))}
          </Select>
          {mode === "customer" && (
            <Select label="조회 기간" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
              {[7, 30, 90].map((d) => (
                <option value={d} key={d}>
                  최근 {d}일
                </option>
              ))}
            </Select>
          )}
        </FilterBar>
      </div>
      <SeoNotice>검색 API 기준 순위이며 네이버 화면 순서와 다를 수 있음</SeoNotice>
      {mode === "customer" ? (
        customerId ? (
          <CustomerCompetition key={customerId + area} customerId={customerId} area={area} days={days} canRefresh={can(me, "COLLECTOR_MANAGE")} />
        ) : customers.isLoading ? (
          <Loading />
        ) : (
          <Panel title="고객사 키워드">
            <Empty>고객사가 없습니다.</Empty>
          </Panel>
        )
      ) : (
        <ProbeMode area={area} />
      )}
      <p className="competition-footnote">공개 페이지의 집계 값만 비교합니다. 네이버의 순위 방식은 공개되지 않았으며 이 결과는 순위의 원인을 설명하지 않습니다.</p>
    </div>
  );
}

const areaLabel = (a: string) => competitionAreas[a] ?? a;

/* ---------------- 고객사 키워드 ---------------- */

function CustomerCompetition({ customerId, area, days, canRefresh }: { customerId: string; area: string; days: number; canRefresh: boolean }) {
  const keywordsQ = useAdminSeoKeywords({ customerId });
  const keywords = (keywordsQ.data?.items ?? []).filter((k) => k.status === "tracking" && (k.areas?.length ? k.areas : ["blog"]).includes(area));
  const [keywordId, setKeywordId] = useState("");
  const kid = keywords.some((k) => k.id === keywordId) ? keywordId : (keywords[0]?.id ?? "");
  const sources = useCompetitionSources(customerId, days, area);
  const view = useCompetitionKeyword(customerId, kid, area, days);
  const refresh = useAction(() => api.post(`/admin/seo/competition/customers/${customerId}/keywords/${kid}/refresh?area=${area}`), [["seo", "competition", customerId]]);
  const resultRef = useRef<HTMLDivElement>(null);
  // 작성처 펼침의 키워드 칩 → 그 키워드로 바꾸고 아래 결과로 이동
  const choose = (word: string) => {
    const k = keywords.find((x) => x.keyword === word);
    if (!k) return;
    setKeywordId(k.id);
    resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    resultRef.current?.focus({ preventScroll: true });
  };

  if (keywordsQ.isLoading) return <Loading />;
  if (!keywords.length)
    return (
      <Panel title="고객사 키워드">
        <Empty>{areaLabel(area)} 영역에서 추적 중인 키워드가 없습니다. 게시 작업 등록 때 키워드를 입력하면 추적이 시작됩니다.</Empty>
      </Panel>
    );
  return (
    <>
      {sources.error ? (
        <PanelError error={errorMessage(sources.error)} onRetry={() => void sources.refetch()} />
      ) : sources.data ? (
        <CompetitionSourceBoard key={customerId + area + days} data={sources.data} onKeyword={choose} />
      ) : (
        <Loading />
      )}
      <div ref={resultRef} tabIndex={-1} className="competition-detail-focus">
        <div className="competition-keyword-picker">
          <BookOpen size={18} />
          <Select label="분석할 키워드" value={kid} onChange={(e) => setKeywordId(e.target.value)}>
            {keywords.map((k) => (
              <option key={k.id} value={k.id}>
                {k.keyword}
              </option>
            ))}
          </Select>
        </div>
        {refresh.error && (
          <div className="competition-error" role="alert">
            <strong>다시 분석하지 못했습니다</strong>
            <p>{errorMessage(refresh.error)}</p>
          </div>
        )}
        {view.error ? (
          <PanelError error={errorMessage(view.error)} onRetry={() => void view.refetch()} />
        ) : view.data ? (
          <KeywordCompetitionView key={kid + area} view={view.data} canRefresh={canRefresh} onRefresh={() => refresh.mutate(undefined)} refreshing={refresh.isPending} />
        ) : (
          <Loading />
        )}
      </div>
    </>
  );
}

/* ---------------- 자유 분석 ---------------- */

function ProbeMode({ area }: { area: string }) {
  const probes = useProbes();
  const [draft, setDraft] = useState("");
  const [formError, setFormError] = useState("");
  const [id, setId] = useState("");
  const create = useAction((body: { keyword: string; areas: string[] }) => api.post<ProbeView>("/admin/seo/probes", body), [["seo", "probes"]]);
  const probe = useProbe(id, area);
  const recent = probes.data?.items ?? [];
  // 조회 자체가 실패(연결 없음·한도)하면 결과 글 없이 error 만 남는다
  const failed = create.error ? errorMessage(create.error) : probe.data?.error && !probe.data.items.length ? probe.data.error : "";

  return (
    <>
      <Panel title="자유 분석" extra={<Badge>등록하지 않은 키워드</Badge>}>
        <form
          className="competition-probe-form"
          onSubmit={(e) => {
            e.preventDefault();
            const keyword = draft.trim();
            if (!keyword) return setFormError("조회할 키워드를 입력해 주세요.");
            setFormError("");
            create.mutate({ keyword, areas: [area] }, { onSuccess: (r) => setId(r.id) });
          }}
        >
          <Field
            label="키워드"
            placeholder="예: 강남 한의원"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setFormError("");
            }}
            error={formError}
            maxLength={100}
          />
          <Button type="submit" disabled={create.isPending}>
            <Search size={15} />
            {create.isPending ? "조회 중" : "조회"}
          </Button>
        </form>
        <p className="competition-caption">선택한 검색 영역을 조회할 때 검색 API 한도 1회를 사용합니다. 글 지표는 순서대로 수집합니다.</p>
        {failed && (
          <div className="competition-error" role="alert">
            <strong>자유 분석을 조회하지 못했습니다</strong>
            <p>{failed}</p>
            <span>입력한 키워드는 유지됩니다.</span>
          </div>
        )}
        {recent.length > 0 && (
          <details className="competition-recent">
            <summary>
              <Clock3 size={15} />
              최근 자유 분석 <Badge>{recent.length}</Badge>
              <ChevronDown size={14} />
            </summary>
            <div>
              {recent.map((r) => (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => {
                    setId(r.id);
                    setDraft(r.keyword);
                    create.reset();
                  }}
                >
                  <b>{r.keyword}</b>
                  <span>{r.error ? r.error : Object.entries(r.totals ?? {}).map(([a, n]) => `${areaLabel(a)} · ${num(n)}건`).join(" · ")}</span>
                  <small>
                    {r.createdByName ?? "—"} · {fmtDateTime(r.createdAt)}
                  </small>
                  <ArrowUpRight size={14} />
                </button>
              ))}
            </div>
          </details>
        )}
      </Panel>
      {failed ? null : !id ? (
        <Panel title="키워드를 바로 살펴보세요">
          <Empty>조회할 키워드를 입력해 주세요. 고객사 등록 없이 상위 글의 공통점을 살펴볼 수 있습니다.</Empty>
        </Panel>
      ) : probe.error ? (
        <PanelError error={errorMessage(probe.error)} onRetry={() => void probe.refetch()} />
      ) : !probe.data ? (
        <Loading />
      ) : !probe.data.areas.includes(area) ? (
        <Panel title={`“${probe.data.keyword}”`}>
          <Empty>이 분석은 {probe.data.areas.map(areaLabel).join("·")} 영역만 조회했습니다. 검색 영역을 바꾸거나 새로 조회하세요.</Empty>
        </Panel>
      ) : (
        <KeywordCompetitionView key={id + area} view={probe.data} free />
      )}
    </>
  );
}
